import { ViewportScroller } from '@angular/common';
import {
  APP_INITIALIZER,
  ApplicationConfig,
  ErrorHandler,
  inject,
  provideZoneChangeDetection,
} from '@angular/core';
import {
  NavigationEnd,
  Router,
  provideRouter,
  withInMemoryScrolling,
  withNavigationErrorHandler,
  withPreloading,
  withViewTransitions,
} from '@angular/router';
import { filter } from 'rxjs/operators';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { routes } from './app.routes';
import { guestSessionInterceptor } from './shared/interceptors/guest-session.interceptor';
import { landingAuthInterceptor } from './shared/interceptors/landing-auth.interceptor';
import { landingCsrfInterceptor } from './shared/interceptors/landing-csrf.interceptor';
import {
  ChunkAwareErrorHandler,
  isChunkLoadError,
  reloadOnceForStaleChunk,
} from './shared/error-handling/chunk-load-error';
import { RudderstackService } from './shared/services/rudderstack.service';
import { ReferralAttributionService } from './shared/services/referral-attribution.service';
import { SeoService } from './shared/services/seo.service';
import { primeNgProviders } from './primeng.providers';
import { IdlePreloading } from './shared/routing/idle-preloading';

/**
 * Initialiser factory — wires Angular's router events to the
 * RudderStack `page()` call so every SPA navigation produces a
 * client-side pageview, not just the initial document load.
 *
 * Why APP_INITIALIZER and not a constructor in `AppComponent`?
 *   - APP_INITIALIZER runs before bootstrap finishes, so the very
 *     first `NavigationEnd` (the initial route) is captured.
 *   - It also keeps the analytics wiring out of `AppComponent`, which
 *     stays focused on layout / shell concerns.
 *
 * The initialiser also fires ONE guaranteed boot-time `track` event
 * (`Landing: Boot`) right after bootstrap so we always see at least
 * one event per session in the RudderStack dashboard — handy for
 * proving end-to-end connectivity even when the visitor bounces
 * before scrolling. After ~3.5s we also peek at the SDK and emit a
 * console warning if it never finished loading (the classic
 * "ad-blocker stripped cdn.rudderlabs.com" symptom), so the next
 * dev who opens DevTools knows exactly why their events vanished.
 */
function initAnalyticsTracking(): () => void {
  return () => {
    const router = inject(Router);
    const rudder = inject(RudderstackService);

    router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe((event) => {
        rudder.page(undefined, {
          path: event.urlAfterRedirects,
        });
      });

    if (typeof window !== 'undefined') {
      // Smoke event — proves the pipeline is wired up even if the
      // visitor never scrolls past the fold.
      rudder.track('Landing: Boot', {
        section: 'App',
        userAgent: navigator?.userAgent,
      });

      window.setTimeout(() => {
        if (!rudder.isReady()) {
          // eslint-disable-next-line no-console
          console.warn(
            '[rudder] SDK did not finish loading after 3.5s — most likely an ' +
              'ad-blocker / privacy extension is blocking cdn.rudderlabs.com. ' +
              'Disable it for aiskilled.co and reload to confirm.',
          );
        }
      }, 3500);
    }
  };
}

function initSeo(): () => void {
  return () => {
    inject(SeoService).init();
  };
}

/** Sections reached by a link (`/company#contact`) stop below the floating header, not under it. */
function initAnchorOffset(): () => void {
  return () => {
    const scroller = inject(ViewportScroller);
    scroller.setOffset(() => {
      if (typeof window === 'undefined') return [0, 0];
      const header = document.querySelector('header.sk-nav');
      return [0, header ? Math.round(header.getBoundingClientRect().bottom + 16) : 0];
    });
  };
}

function initReferralAttribution(): () => void {
  return () => {
    inject(ReferralAttributionService).init();
  };
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    ...primeNgProviders,
    provideHttpClient(
      withInterceptors([guestSessionInterceptor, landingCsrfInterceptor, landingAuthInterceptor]),
    ),
    provideRouter(
      routes,
      withInMemoryScrolling({
        scrollPositionRestoration: 'top',
        anchorScrolling: 'enabled',
      }),
      // pages cross-fade into each other (identity.css); the header holds still
      withViewTransitions({ skipInitialTransition: true }),
      // the other pages' code arrives in the background once this one has settled
      withPreloading(IdlePreloading),
      // Router-level catch for lazy-route loading failures right after
      // a deploy ("Failed to fetch dynamically imported module …").
      // We reload once so the browser picks up the new chunk hashes
      // instead of leaving the visitor on a blank route.
      withNavigationErrorHandler((err) => {
        if (isChunkLoadError(err)) {
          reloadOnceForStaleChunk();
        }
      }),
    ),
    { provide: ErrorHandler, useClass: ChunkAwareErrorHandler },
    {
      provide: APP_INITIALIZER,
      useFactory: initAnalyticsTracking,
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initSeo,
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initReferralAttribution,
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initAnchorOffset,
      multi: true,
    },
  ],
};
